import { useState, useEffect } from "react";
import { Loader2, CheckCircle2, ArrowLeft, ShoppingCart, Repeat } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PRODUCTS, INDUSTRIES, COMPANY } from "../lib/data";
import api, { formatApiErrorDetail } from "../lib/api";
import { getVisitorId, getAttribution } from "../lib/track";
import { Reveal } from "../components/site/Reveal";

// Values must match the backend BUDGET_RANGE_VALUES / TIMELINE_CLOSE_DAYS keys exactly.
const JOB_TITLES = ["Owner / Executive", "Safety / HSE Director", "Procurement / Purchasing",
  "Operations Manager", "Superintendent / Foreman", "Engineer", "Fleet / Asset Manager", "Other"];
const TIMELINES = ["Immediate (0–30 days)", "1–3 months", "3–6 months", "6–12 months", "Just researching"];
const BUDGET_RANGES = ["Under $25k", "$25k – $100k", "$100k – $250k", "$250k – $500k", "$500k+"];
const REFERRAL_SOURCES = ["Search engine (Google, etc.)", "Referral / word of mouth", "LinkedIn",
  "Trade show / event", "Email", "Existing customer", "Other"];
const AVENUE_OPTIONS = [
  { key: "buy", value: "Buy the Mats (purchase)", title: "Purchase", sub: "Own the mats · you deploy & reuse", icon: ShoppingCart },
  { key: "rent", value: "Rent the Mats (rental)", title: "Rent", sub: "We deliver, install & remove — turnkey", icon: Repeat },
];

import { Seo } from "../components/site/Seo";

export default function RequestQuote() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", job_title: "", industry: "", avenue: "",
    products: [], fleet_size: "", num_jobsites: "", timeline: "", budget_range: "",
    referral_source: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const match = AVENUE_OPTIONS.find((o) => o.key === searchParams.get("avenue"));
    if (match) setForm((f) => ({ ...f, avenue: match.value }));
    const source = searchParams.get("source");
    if (source === "mat-selector" || source === "mat-catalog") {
      const mat = searchParams.get("mat") || "";
      const detail = searchParams.get("detail") || "";
      setForm((f) => ({
        ...f,
        industry: source === "mat-selector" ? "Ground Protection Mat Selector" : "Ground Protection Mat Catalog",
        message: detail || (mat ? `I'm interested in ${mat} for a ground protection / site access project.` : f.message),
      }));
    }
  }, [searchParams]);

  const prefilledMat = searchParams.get("mat");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleProduct = (name) =>
    setForm((f) => ({ ...f, products: f.products.includes(name) ? f.products.filter((p) => p !== name) : [...f.products, name] }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/quote", { ...form, visitor_id: getVisitorId(), attribution: getAttribution() });
      setSent(true);
      toast.success("Quote request sent", { description: data.message });
    } catch (err) {
      toast.error("Something went wrong", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="request-quote-page" className="pt-[72px]">
      <Seo title="Request a Quote" description="Get a fast, tailored quote on industrial absorbents, water treatment, and ground protection matting from OneHarvest." />
      <section className="bg-ink relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-10" />
        <div className="container-x py-20 relative">
          <div className="kicker text-flame mb-4">Request a Quote</div>
          <h1 className="font-display font-black text-white text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
            Let's build your readiness plan.
          </h1>
          <p className="mt-5 text-white/70 text-lg max-w-2xl">
            Share a few details and your Environmental Solutions Manager will follow up within one business day with customized recommendations.
          </p>
          {prefilledMat && (
            <div data-testid="prefill-notice" className="mt-6 inline-flex items-center gap-2 border border-flame/40 bg-flame/10 px-4 py-2.5 text-sm text-white">
              <span className="text-flame font-semibold">Prefilled:</span> {prefilledMat} — we've added your selection details below.
            </div>
          )}
        </div>
      </section>

      <section className="bg-background">
        <div className="container-x py-24 max-w-3xl">
          {sent ? (
            <div data-testid="quote-success" className="border border-border bg-card p-12 text-center">
              <CheckCircle2 className="h-14 w-14 text-flame mx-auto" />
              <h2 className="mt-6 font-display font-bold text-2xl text-ink">Request received.</h2>
              <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                Your Environmental Solutions Manager will follow up within one business day. For urgent needs, call {COMPANY.phone}.
              </p>
              <Link to="/" className="mt-8 inline-flex items-center gap-2 text-flame font-semibold">
                <ArrowLeft className="h-4 w-4" /> Back to home
              </Link>
            </div>
          ) : (
            <Reveal>
              <form onSubmit={submit} data-testid="quote-form" className="border border-border bg-card p-8 md:p-10 space-y-6">
                <div>
                  <label className="kicker text-charcoal block mb-3">How would you like to engage? *</label>
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="avenue-chooser">
                    {AVENUE_OPTIONS.map((opt) => {
                      const active = form.avenue === opt.value;
                      return (
                        <button type="button" key={opt.key} data-testid={`avenue-${opt.key}`}
                          onClick={() => setForm({ ...form, avenue: opt.value })}
                          className={`flex items-start gap-3 border p-4 text-left transition-colors ${active ? "border-flame bg-flame/5" : "border-input hover:border-flame/50"}`}>
                          <opt.icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${active ? "text-flame" : "text-charcoal"}`} />
                          <span className="min-w-0">
                            <span className="block font-display font-bold text-ink text-sm">{opt.title}</span>
                            <span className="block text-xs text-muted-foreground mt-0.5">{opt.sub}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name *" testid="quote-name" value={form.name} onChange={set("name")} required />
                  <Field label="Email *" testid="quote-email" type="email" value={form.email} onChange={set("email")} required />
                  <Field label="Company *" testid="quote-company" value={form.company} onChange={set("company")} required />
                  <SelectField label="Job title / role" testid="quote-job-title" value={form.job_title} onChange={set("job_title")} placeholder="Select role" options={JOB_TITLES} />
                  <Field label="Phone" testid="quote-phone" value={form.phone} onChange={set("phone")} />
                  <div>
                    <label className="kicker text-charcoal block mb-2">Industry</label>
                    <select data-testid="quote-industry" value={form.industry} onChange={set("industry")}
                      className="w-full border border-input bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-flame">
                      <option value="">Select industry</option>
                      {form.industry && !INDUSTRIES.some((i) => i.name === form.industry) && (
                        <option value={form.industry}>{form.industry}</option>
                      )}
                      {INDUSTRIES.map((i) => <option key={i.slug} value={i.name}>{i.name}</option>)}
                    </select>
                  </div>
                  <Field label="Fleet / crew size" testid="quote-fleet" value={form.fleet_size} onChange={set("fleet_size")} placeholder="e.g. 12 trucks" />
                  <Field label="Active jobsites / projects" testid="quote-jobsites" value={form.num_jobsites} onChange={set("num_jobsites")} placeholder="e.g. 5" />
                  <SelectField label="Project timeline" testid="quote-timeline" value={form.timeline} onChange={set("timeline")} placeholder="Select timeline" options={TIMELINES} />
                  <SelectField label="Estimated budget" testid="quote-budget" value={form.budget_range} onChange={set("budget_range")} placeholder="Select budget range" options={BUDGET_RANGES} />
                </div>

                <SelectField label="How did you hear about us?" testid="quote-referral" value={form.referral_source} onChange={set("referral_source")} placeholder="Select source" options={REFERRAL_SOURCES} />

                <div>
                  <label className="kicker text-charcoal block mb-3">Products of interest</label>
                  <div className="flex flex-wrap gap-2">
                    {PRODUCTS.map((p) => {
                      const active = form.products.includes(p.name);
                      return (
                        <button type="button" key={p.slug} data-testid={`quote-product-${p.slug}`} onClick={() => toggleProduct(p.name)}
                          className={`px-4 py-2 text-sm border transition-colors ${active ? "bg-flame text-white border-flame" : "bg-white text-charcoal border-input hover:border-flame"}`}>
                          {p.name}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="kicker text-charcoal block mb-2">Tell us about your operation</label>
                  <textarea data-testid="quote-message" rows={4} value={form.message} onChange={set("message")}
                    className="w-full border border-input bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-flame resize-none" />
                </div>

                <button data-testid="quote-submit" disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors disabled:opacity-60">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : "Submit Quote Request"}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, required, placeholder }) {
  return (
    <div>
      <label className="kicker text-charcoal block mb-2">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange} required={required} placeholder={placeholder}
        className="w-full border border-input bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-flame" />
    </div>
  );
}

function SelectField({ label, testid, value, onChange, placeholder, options }) {
  return (
    <div>
      <label className="kicker text-charcoal block mb-2">{label}</label>
      <select data-testid={testid} value={value} onChange={onChange}
        className="w-full border border-input bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-flame">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
